import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { auth, setActiveWorkspaceId, setAuthToken, workspaceApi } from "@/lib/api";
import type { Workspace } from "@/types";

// ─── Workspace Change Event System ────────────────────────────────────────────
/** Fired when the active workspace changes; listeners can use this to refresh data */
export const WORKSPACE_CHANGED = "workspace:changed";

interface WorkspaceChangeEvent {
  previousWorkspaceId: string | null;
  newWorkspaceId: string;
}

export function onWorkspaceChange(callback: (event: WorkspaceChangeEvent) => void): () => void {
  const handler = (e: Event) => {
    if (e instanceof CustomEvent) {
      callback(e.detail as WorkspaceChangeEvent);
    }
  };
  window.addEventListener(WORKSPACE_CHANGED, handler);
  return () => window.removeEventListener(WORKSPACE_CHANGED, handler);
}

export function dispatchWorkspaceChange(previousId: string | null, newId: string) {
  window.dispatchEvent(
    new CustomEvent<WorkspaceChangeEvent>(WORKSPACE_CHANGED, {
      detail: { previousWorkspaceId: previousId, newWorkspaceId: newId },
    })
  );
}

interface AuthUser {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
  isAdmin?: boolean;
}

interface WorkspaceListItem {
  id: string;
  name: string;
  slug: string;
  domain: string | null;
  logoUrl: string | null;
  plan: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  workspace: Workspace | null;
  workspaces: WorkspaceListItem[];
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<{ error?: string }>;
  signup: (email: string, password: string, name: string, workspaceName: string) => Promise<{ error?: string }>;
  logout: () => void;
  refreshWorkspace: () => Promise<void>;
  updateUser: (patch: { name?: string; avatarUrl?: string | null }) => Promise<void>;
  switchWorkspace: (workspaceId: string) => Promise<{ error?: string }>;
  adoptWorkspaceSession: (session: { token: string; id: string; name: string; slug: string; domain: string | null; logoUrl: string | null; plan: string; trialEndsAt: string | null }) => void;
  createWorkspace: (name: string, domain?: string | null) => Promise<{ error?: string; created?: boolean }>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [workspaces, setWorkspaces] = useState<WorkspaceListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadWorkspaceList = useCallback(async () => {
    const res = await workspaceApi.list();
    if ("data" in res && res.data) setWorkspaces(res.data);
  }, []);

  // Load full workspace details including domain/logoUrl
  const loadWorkspaceDetails = useCallback(async () => {
    const res = await workspaceApi.get();
    if ("data" in res && res.data) {
      setWorkspace(prev => prev ? {
        ...prev,
        domain: res.data!.domain,
        logoUrl: res.data!.logoUrl,
      } : null);
    }
  }, []);

  const loadMe = useCallback(async () => {
    // Auth state is now managed entirely by HttpOnly cookie
    // No need to check for localStorage token — if cookie is valid, /auth/me will succeed
    const result = await auth.me();
    if ("data" in result && result.data) {
      setUser(result.data.user);
      
      // Only set workspace if one exists; if none, workspace is null and redirect to onboarding
      if (result.data.workspace) {
        setWorkspace(result.data.workspace as Workspace);
        const wsRes = await workspaceApi.get();
        if ("data" in wsRes && wsRes.data) {
          setWorkspace({ ...result.data.workspace, domain: wsRes.data.domain, logoUrl: wsRes.data.logoUrl } as Workspace);
        }
      } else {
        setWorkspace(null);
      }
      
      // Load workspace list after setting user/workspace
      const wsList = await workspaceApi.list();
      if ("data" in wsList && wsList.data) setWorkspaces(wsList.data);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const refreshWorkspace = useCallback(async () => {
    await loadWorkspaceDetails();
  }, [loadWorkspaceDetails]);

  const updateUser = useCallback(async (patch: { name?: string; avatarUrl?: string | null }) => {
    const res = await auth.updateProfile(patch);
    if ("data" in res && res.data) {
      setUser(prev => prev ? { ...prev, ...res.data } : null);
    }
  }, []);

  const login = async (email: string, password: string) => {
    const result = await auth.login(email, password);
    if ("error" in result) return { error: result.error?.message ?? "Login failed" };
    if (!result.data) return { error: "Login failed" };
    // HttpOnly cookie is set by backend — no need to store token in localStorage
    setUser(result.data.user);
    setWorkspace(result.data.workspace as Workspace);
    setActiveWorkspaceId(result.data.workspace.id);
    const wsRes = await workspaceApi.get();
    if ("data" in wsRes && wsRes.data) {
      setWorkspace({ ...result.data.workspace, domain: wsRes.data.domain, logoUrl: wsRes.data.logoUrl } as Workspace);
    }
    loadWorkspaceList();
    return {};
  };

  const signup = async (email: string, password: string, name: string, workspaceName: string) => {
    const result = await auth.signup(email, password, name, workspaceName);
    if ("error" in result) return { error: result.error?.message ?? "Signup failed" };
    if (!result.data) return { error: "Signup failed" };
    // HttpOnly cookie is set by backend — no need to store token in localStorage
    setUser(result.data.user);
    setWorkspace(result.data.workspace as Workspace);
    return {};
  };

  const switchWorkspace = useCallback(async (workspaceId: string) => {
    const previousWorkspaceId = workspace?.id ?? null;
    const res = await workspaceApi.switch(workspaceId);
    if ("data" in res && res.data) {
      // Backend issues a new JWT (new wid) as both a Set-Cookie and in the response body.
      // Update the localStorage Bearer token too so it stays in sync with the cookie.
      // Without this, the old token (old workspace) is used as the Bearer fallback.
      setAuthToken(res.data.token);
      setActiveWorkspaceId(workspaceId);
      // Update state immediately so sidebar reflects change
      setWorkspace(res.data.workspace as Workspace);
      // Reload workspace list
      await loadWorkspaceList();
      // Emit workspace change event so pages can refresh their data
      dispatchWorkspaceChange(previousWorkspaceId, workspaceId);
      return {};
    }
    return { error: "error" in res ? res.error?.message ?? "Failed to switch workspace" : "Failed to switch workspace" };
  }, [loadWorkspaceList, workspace?.id]);

  const adoptWorkspaceSession = useCallback((session: { token: string; id: string; name: string; slug: string; domain: string | null; logoUrl: string | null; plan: string; trialEndsAt: string | null }) => {
    // HttpOnly cookie is already set by backend — no need to store token in localStorage
    setActiveWorkspaceId(session.id);
    setWorkspace({
      id: session.id, name: session.name, slug: session.slug, ownerId: user?.id ?? "",
      plan: session.plan as Workspace["plan"], trialEndsAt: session.trialEndsAt,
      domain: session.domain, logoUrl: session.logoUrl, createdAt: new Date().toISOString(),
    });
  }, [user?.id]);

  const createWorkspace = useCallback(async (name: string, domain?: string | null) => {
    const res = await workspaceApi.create(name, domain);
    if ("error" in res) return { error: res.error?.message ?? "Failed to create workspace" };
    if (!res.data) return { error: "Failed to create workspace" };
    // Switch to the new workspace
    const switchResult = await switchWorkspace(res.data.id);
    if (switchResult.error) {
      return {
        error: `Workspace created, but switching failed: ${switchResult.error}`,
        created: true,
      };
    }
    return {};
  }, [switchWorkspace]);

  const logout = () => {
    // Clear cookie server-side
    auth.logout().catch(() => {});
    setAuthToken(null);
    // Clear local state (HttpOnly cookie is cleared by server via clearAuthCookie())
    setActiveWorkspaceId(null);
    setUser(null);
    setWorkspace(null);
    setWorkspaces([]);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        workspace,
        workspaces,
        isLoading,
        isAuthenticated: !!user,
        login,
        signup,
        logout,
        refreshWorkspace,
        updateUser,
        switchWorkspace,
        adoptWorkspaceSession,
        createWorkspace,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
